import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import {
  cryptoHoldFor,
  cryptoReceiveEst,
  CURRENCY_SYMBOL,
  D,
  defaultDepositCurrency,
  fmt,
  fmtUsd,
  PAYOUT_CURRENCIES,
  PAYOUT_RAIL,
  WITHDRAWAL_ASSETS,
  withdrawalAsset,
  withdrawalAssetOf,
  type AccountSummary,
  type PayoutAccount,
  type PayoutCurrency,
  type Profile,
  type VrActiveBonus,
  type Withdrawal,
  type WithdrawalAddress,
  type WithdrawalAssetId,
  type WithdrawOptions,
} from '@platform/shared';
import { cx } from '../../components/ui';
import { api } from '../../lib/api';
import { IP_COUNTRY } from '../../lib/config';
import { useSearch } from '../../lib/router';
import { useLoad, useVersion } from '../../stores/account';
import { useQuote } from '../../stores/market';
import { useMe } from '../../stores/session';
import { AIcon, Alert, Card, Chip, errMsg, Field, H1, H2, List, Notice, PillButton, PillLink, Row, Segmented, when } from './kit';
import { NumInput } from '../../components/NumInput';
import { AddPayoutAccount } from './withdraw/AddPayoutAccount';
import { coinMoney, money, receivedText, STATUS, WithdrawalIcon, WithdrawalTicket } from './withdraw/WithdrawalTicket';
import { cryptoIcon } from './deposit/coins';
import { Dropdown } from './deposit/CoinPicker';

const short = (a: string) => `${a.slice(0, 6)}…${a.slice(-4)}`;
/** Round badge for the currency, bank and card options (the coin icons' size). */
const Badge = ({ children }: { children: ReactNode }) => (
  <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-pill text-[15px] font-semibold text-fg">{children}</span>
);


type Tab = 'crypto' | 'wire' | 'card';
const tabFrom = (search: string): Tab => {
  const m = new URLSearchParams(search).get('method');
  return m === 'wire' || m === 'bank_wire' ? 'wire' : m === 'card' ? 'card' : 'crypto';
};
const methodOf = (tab: Exclude<Tab, 'crypto'>) => (tab === 'card' ? 'card' : 'bank_wire');
/** Crypto asset from the URL: ?asset=BTC, or ?network=TRC20 / ERC20 (USDT) as linked before. */
const assetFrom = (search: string): WithdrawalAssetId => {
  const p = new URLSearchParams(search);
  return (withdrawalAsset(p.get('asset')) ?? withdrawalAssetOf('USDT', p.get('network')) ?? WITHDRAWAL_ASSETS[0]!).id;
};
/** The withdrawal assets as dropdown options: coin and network, fee and speed. */
const assetOptions = (options: WithdrawOptions | null) =>
  WITHDRAWAL_ASSETS.map((a) => {
    const o = options?.crypto.find((x) => x.id === a.id);
    return {
      id: a.id,
      icon: cryptoIcon(a.coin, a.network),
      title: a.label,
      sub: `Fee ${a.coin === 'USDT' ? fmt(o?.fee ?? a.fee, 2) : (o?.fee ?? a.fee)} ${a.coin} · ${a.eta}`,
      meta: a.coin !== 'USDT' && o?.rate ? fmtUsd(o.rate) : undefined,
    };
  });

/** Sign in the amount box when the amount is entered in the coin. */
const COIN_SIGN: Record<string, string> = { BTC: '₿', ETH: 'Ξ' };

const CURRENCY_NAME: Record<PayoutCurrency, string> = { USD: 'US dollar', EUR: 'Euro', GBP: 'British pound' };

const CHECKLIST: Record<Tab, string[]> = {
  crypto: [
    'Only free margin can be withdrawn. Open positions keep their margin.',
    'We pay USDT on Tron (TRC20) or Ethereum (ERC20), BTC and ETH. Send only to an address for the same coin and network.',
    'BTC and ETH are converted from your USDT balance at the live rate. Enter the amount in USD to fix what leaves your balance, or in BTC / ETH to fix exactly what you receive. The network fee is taken in the coin.',
    'A new address can be used as soon as you save it. We email you when one is added.',
    'The amount leaves your balance when you request it and comes back if the request is cancelled or rejected.',
  ],
  wire: [
    'The bank account must be in your own name. We cannot pay a third party.',
    'EUR and GBP are converted from your USDT balance. The rate shown is indicative; the final rate is set on the day we pay.',
    'GBP usually arrives the same day, SEPA in 1 business day, SWIFT in 1 to 5 business days.',
    'A new bank account can be used as soon as you save it. We email you when one is added.',
  ],
  card: [
    'We refund to a card you have already deposited with.',
    'We only store the last 4 digits. Never send your full card number or CVV.',
    'Card refunds usually show on your statement in 3 to 10 business days.',
    'If your card cannot take the refund, we contact you and pay by wire instead.',
  ],
};

function AddAddress({ initial, options, onAdded, onCancel }: { initial: WithdrawalAssetId; options: WithdrawOptions | null; onAdded: (a: WithdrawalAddress) => void; onCancel: () => void }) {
  const [label, setLabel] = useState('');
  const [assetId, setAssetId] = useState<WithdrawalAssetId>(initial);
  const [address, setAddress] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const asset = withdrawalAsset(assetId)!;
  const bad = address.trim() !== '' && !asset.check(address.trim());
  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      onAdded(await api.post<WithdrawalAddress>('/api/withdrawals/addresses', { label, asset: assetId, address: address.trim() }));
    } catch (x) {
      setErr(errMsg(x));
    } finally {
      setBusy(false);
    }
  };
  return (
    // A div, not a form: this sits inside the withdrawal form.
    <div role="group" aria-label="Add a withdrawal address" className="flex flex-col gap-5 rounded-[20px] bg-track p-5 md:p-6">
      <b className="text-[16px]">Add a withdrawal address</b>
      <div className="grid items-start gap-5 md:grid-cols-2">
        <Field label="Name" htmlFor="wa-label">
          <input id="wa-label" className="field" placeholder="e.g. Ledger cold wallet" maxLength={40} value={label} onChange={(e) => setLabel(e.target.value)} />
        </Field>
        <Dropdown label="Coin and network" value={assetId} onChange={(v) => setAssetId(v as WithdrawalAssetId)} options={assetOptions(options)} />
      </div>
      <Field label="Address" htmlFor="wa-address" error={bad ? `That is not a valid ${asset.label} address. Enter ${asset.addressHint}.` : err} hint={bad || err ? undefined : `Only a ${asset.coin} address on the ${asset.chain} (${asset.network}) network.`}>
        <input id="wa-address" required className="field num !text-[15px]" value={address} onChange={(e) => setAddress(e.target.value)} />
      </Field>
      <p className="m-0 text-[14px] text-muted">You can withdraw to it as soon as you save it. For your security, we email you when one is added.</p>
      <div className="flex gap-3">
        <PillButton size="sm" busy={busy} disabled={!address.trim() || bad} onClick={() => void submit()}>
          Save address
        </PillButton>
        <PillButton kind="secondary" size="sm" onClick={onCancel}>
          Cancel
        </PillButton>
      </div>
    </div>
  );
}

export function WithdrawPage() {
  const me = useMe()!;
  const search = useSearch();
  const live = me.accounts.find((a) => a.kind === 'live')!;
  const verified = me.kycStatus === 'approved';
  const balV = useVersion('balance');
  const wV = useVersion('withdrawal');
  const { data: summary, reload: reloadSummary } = useLoad<AccountSummary>(`/api/accounts/${live.id}/summary`, [balV]);
  const { data: addresses, setData: setAddresses } = useLoad<WithdrawalAddress[]>('/api/withdrawals/addresses');
  const { data: payouts, setData: setPayouts } = useLoad<PayoutAccount[]>('/api/withdrawals/payout-accounts');
  const { data: options } = useLoad<WithdrawOptions>('/api/withdrawals/options');
  const { data: profile } = useLoad<Profile>('/api/profile');
  const { data: list, reload: reloadList } = useLoad<Withdrawal[]>('/api/withdrawals', [wV]);
  // Volume rewards (D5): a withdrawal request removes an active trading bonus, so say so first.
  const { data: bonus, reload: reloadBonus } = useLoad<VrActiveBonus>('/api/volume-rewards/bonus');
  const acc = summary ?? live;

  const [tab, setTab] = useState<Tab>(() => tabFrom(search));
  const [currency, setCurrency] = useState<PayoutCurrency | null>(() => {
    const c = new URLSearchParams(search).get('currency');
    return PAYOUT_CURRENCIES.includes(c as PayoutCurrency) ? (c as PayoutCurrency) : null;
  });
  // Until the client picks one, the currency follows where they're connecting from (IP), then their profile.
  const ccy: PayoutCurrency = currency ?? defaultDepositCurrency(IP_COUNTRY || profile?.country);
  const fiat = tab !== 'crypto';
  const kind = tab === 'card' ? 'card' : 'bank';

  const [assetId, setAssetId] = useState<WithdrawalAssetId>(() => assetFrom(search));
  const [addressId, setAddressId] = useState('');
  const [payoutId, setPayoutId] = useState('');
  const [adding, setAdding] = useState(false);
  const [amount, setAmount] = useState(() => new URLSearchParams(search).get('amount') ?? '');
  // BTC / ETH: the amount box takes USD (default) or the coin; switching converts what is typed.
  const [unit, setUnit] = useState<'USD' | 'coin'>('USD');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  // The withdrawal whose ticket is open; read from the list so it follows live updates.
  // ?wd=<id> (a notification's link) opens that withdrawal's ticket.
  const [ticketId, setTicketId] = useState<string | null>(() => new URLSearchParams(search).get('wd'));
  const ticket = (list ?? []).find((w) => w.id === ticketId) ?? null;

  // Saved addresses for the chosen coin and network; keep the pick valid when either changes.
  const cryptoChoices = useMemo(() => (addresses ?? []).filter((a) => a.asset === assetId), [addresses, assetId]);
  useEffect(() => {
    if (!cryptoChoices.some((a) => a.id === addressId)) setAddressId(cryptoChoices[0]?.id ?? '');
  }, [cryptoChoices, addressId]);

  // Saved bank accounts (wire) or cards (card) in the chosen currency; keep the pick valid when either changes.
  const choices = useMemo(() => (payouts ?? []).filter((p) => p.kind === kind && p.currency === ccy), [payouts, kind, ccy]);
  useEffect(() => {
    if (!choices.some((p) => p.id === payoutId)) setPayoutId(choices[0]?.id ?? '');
  }, [choices, payoutId]);
  useEffect(() => setAdding(false), [tab, ccy]);

  const addr = cryptoChoices.find((a) => a.id === addressId) ?? null;
  const payout = choices.find((p) => p.id === payoutId) ?? null;
  const asset = withdrawalAsset(assetId)!;
  const usdt = asset.coin === 'USDT';
  const assetOpt = options?.crypto.find((a) => a.id === assetId) ?? null;
  const dest = fiat ? payout : addr;

  const cardBlocked = tab === 'card' && options !== null && !options.cardEligible;
  // Fiat: USD fee from the amount. Crypto: the fee in the coin (USDT 1:1 with USD).
  const fee = fiat ? (options?.fees[methodOf(tab)][ccy] ?? null) : (assetOpt?.fee ?? asset.fee);
  const lim = options?.limits[fiat ? methodOf(tab) : 'crypto'] ?? null;
  const min = fiat ? (lim?.min ?? null) : (assetOpt?.min ?? D.max(asset.min, lim?.min ?? '0').toString());
  const max = lim?.max ?? null;
  const leftToday = lim?.daily ? D.max(0, D.from(lim.daily).sub(lim.usedToday)) : null;
  // BTC / ETH follow the live mark (the price stream, ≈4 updates a second), the same price the request is quoted at.
  const quote = useQuote(!fiat && !usdt ? asset.coin : '');
  const liveRate = quote && D.from(quote.mark).isPos() ? quote.mark : null;
  const rate = fiat ? (ccy === 'USD' ? '1' : (options?.rates[ccy] ?? null)) : usdt ? '1' : (liveRate ?? assetOpt?.rate ?? null);
  const noPrice = !fiat && !usdt && options !== null && !rate;

  const inCoin = !fiat && !usdt && unit === 'coin';
  const coinAmt = inCoin && new RegExp(`^\\d+(\\.\\d{1,${asset.dp}})?$`).test(amount) && D.from(amount).isPos() ? D.from(amount) : null;
  // Entered in the coin, the USD figure is what will be held: (coin + fee) × rate, rounded up to the cent.
  const amt = inCoin ? (coinAmt && rate ? cryptoHoldFor(coinAmt, rate, fee!) : null) : /^\d+(\.\d{1,2})?$/.test(amount) ? D.from(amount) : null;
  const free = D.from(acc.withdrawable);
  const receive = fiat ? (amt && fee !== null && amt.gt(fee) ? amt.sub(fee) : null) : inCoin ? coinAmt : amt && rate ? cryptoReceiveEst(asset, amt, rate, fee!) : null;
  /** Coin amount (as typed) for a USD figure: what that many dollars buys after the fee, rounded down. */
  const coinFor = (usd: D) => (rate ? (cryptoReceiveEst(asset, usd, rate, fee!)?.toString() ?? '') : '');
  const switchUnit = (u: 'USD' | 'coin') => {
    if (u === unit) return;
    // Keep what was typed: convert it, so the client never loses their amount.
    if (amt && receive && rate) setAmount(u === 'coin' ? receive.toString() : amt.toFixed(2));
    else setAmount('');
    setUnit(u);
  };
  const receiveEst = fiat && receive && rate ? receive.div(rate, 'down') : null;
  const levelAfter = useMemo(() => {
    if (!amt || D.from(acc.marginUsed).isZero()) return null;
    return D.from(acc.equity).sub(amt).mul(100).div(acc.marginUsed);
  }, [amt, acc]);
  const amountErr = !amount
    ? null
    : inCoin && !coinAmt
      ? `Enter an amount in ${asset.coin} with up to ${asset.dp} decimals`
      : !amt
        ? inCoin
          ? null
          : 'Enter an amount with up to 2 decimals'
      : amt.gt(free)
        ? inCoin
          ? `You can withdraw up to ${coinFor(free)} ${asset.coin} (${fmtUsd(free)})`
          : `You can withdraw up to ${fmtUsd(free)}`
        : min !== null && amt.lt(min)
          ? fiat
            ? `The minimum ${tab === 'card' ? 'card' : 'wire'} withdrawal is ${fmtUsd(min)}`
            : usdt
              ? `The minimum on ${asset.network} is ${fmt(min, 2)} USDT`
              : `The minimum ${asset.coin} withdrawal is ${fmtUsd(min)}`
          : max !== null && amt.gt(max)
            ? `The most you can withdraw at once ${fiat ? `by ${tab === 'card' ? 'card' : 'wire'}` : 'in crypto'} is ${fmtUsd(max)}`
            : leftToday !== null && amt.gt(leftToday)
              ? leftToday.isPos()
                ? `You can withdraw ${fmtUsd(leftToday)} more this way in the next 24 hours`
                : 'You have reached the 24-hour limit for this method. Try again later or use another method.'
              : fiat
                ? fee !== null && !amt.gt(fee)
                  ? `The amount must be more than the ${fmtUsd(fee)} fee`
                  : null
                : rate && !receive
                  ? `The amount must be more than the ${fee} ${asset.coin} network fee`
                  : null;
  const limitsHint = [
    min !== null && D.from(min).isPos() ? `Min ${fiat || !usdt ? fmtUsd(min) : `${fmt(min, 2)} USDT`}` : null,
    max !== null ? `max ${fmtUsd(max)} per withdrawal` : null,
    leftToday !== null ? `${fmtUsd(leftToday)} left in the next 24 hours` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const canSubmit = verified && !cardBlocked && dest && amt && !amountErr && !noPrice && (!me.totpEnabled || code.length >= 6);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy('submit');
    setMsg(null);
    try {
      const to = fiat ? { payoutAccountId: payoutId } : { addressId };
      // In the coin, the USD shown is sent along: the server refuses if the price has since moved it up by more than 0.5%.
      const body = inCoin ? { ...to, amountCoin: amount, quotedAmount: amt?.toFixed(2), code } : { ...to, amount, code };
      const w = await api.post<Withdrawal>('/api/withdrawals', body);
      const get =
        w.method === 'crypto'
          ? w.coinFixed
            ? `exactly ${coinMoney(w.receive, w.coin!)} (${fmtUsd(w.amount)} held from your balance)`
            : `${w.coin === 'USDT' ? '' : 'about '}${coinMoney(w.receive, w.coin ?? 'USDT')}`
          : w.currency === 'USD'
            ? money(w.receive, 'USD')
            : w.receiveEst
              ? `about ${money(w.receiveEst, w.currency)}`
              : `the ${w.currency} value of ${fmtUsd(w.receive)}`;
      setMsg({ ok: true, text: `${w.number} requested. You will receive ${get} after review (within 24 hours).` });
      setAmount('');
      setCode('');
      void reloadList();
      void reloadSummary();
      void reloadBonus();
    } catch (x) {
      setMsg({ ok: false, text: errMsg(x) });
      setCode('');
    } finally {
      setBusy(null);
    }
  };
  const removeAddress = async (id: string) => {
    try {
      await api.del(`/api/withdrawals/addresses/${id}`);
      setAddresses((addresses ?? []).filter((a) => a.id !== id));
      if (addressId === id) setAddressId('');
    } catch (x) {
      setMsg({ ok: false, text: errMsg(x) });
    }
  };
  const removePayout = async (id: string) => {
    try {
      await api.del(`/api/withdrawals/payout-accounts/${id}`);
      setPayouts((payouts ?? []).filter((p) => p.id !== id));
    } catch (x) {
      setMsg({ ok: false, text: errMsg(x) });
    }
  };

  const title = tab === 'crypto' ? 'Withdraw crypto' : tab === 'wire' ? 'Withdraw by wire transfer' : 'Withdraw to card';
  const subtitle =
    tab === 'crypto'
      ? usdt
        ? 'Paid out in USDT from your USDT balance, $1 = 1 USDT.'
        : `Paid out in ${asset.coin} from your USDT balance at the current rate.`
      : tab === 'wire'
        ? 'Paid to a bank account in your name, in USD, EUR or GBP.'
        : 'Refunded to a card you have deposited with, in USD, EUR or GBP.';
  const savedCount = (addresses?.length ?? 0) + (payouts?.length ?? 0);

  return (
    <div className="flex flex-col gap-6">
      <Segmented
        label="Withdrawal method"
        size="lg"
        className="w-full max-w-[580px]"
        options={[
          { id: 'crypto', label: 'Crypto' },
          { id: 'wire', label: 'Wire transfer' },
          { id: 'card', label: 'Card' },
        ]}
        value={tab}
        onChange={(t) => {
          setTab(t);
          setMsg(null);
        }}
      />
      <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-8 xl:grid-cols-[minmax(0,1fr)_minmax(0,500px)] 2xl:grid-cols-[minmax(0,1fr)_minmax(0,564px)]">
        <Card>
          <form onSubmit={submit}>
            <H1>{title}</H1>
            <p className="m-0 mt-2 text-[16px] text-muted md:text-[17px]">{subtitle}</p>
            {!verified && (
              <Alert className="mt-8">
                {me.kycStatus === 'pending'
                  ? 'We are verifying your identity and will be in touch shortly. Withdrawals unlock as soon as compliance approves your documents.'
                  : me.kycStatus === 'partial'
                    ? 'Your identity is partly verified. Send the documents we asked for to enable withdrawals.'
                    : 'Verify your identity to enable withdrawals. You can keep trading and depositing in the meantime.'}
                {me.kycStatus !== 'pending' && (
                  <div className="mt-3">
                    <PillLink to="/account/kyc" size="sm">
                      Verify identity
                    </PillLink>
                  </div>
                )}
              </Alert>
            )}
            {verified && cardBlocked && (
              <Alert tone="info" className="mt-8">
                Card withdrawals go back to a card you have deposited with, so they open after your first card deposit. You can withdraw by wire transfer or crypto now.
                <div className="mt-3 flex flex-wrap gap-3">
                  <PillButton size="sm" onClick={() => setTab('wire')}>
                    Withdraw by wire
                  </PillButton>
                  <PillLink to="/account/deposit?method=card" size="sm" kind="secondary">
                    Deposit by card
                  </PillLink>
                </div>
              </Alert>
            )}
            <div className="mt-8 grid gap-2 sm:grid-cols-3 sm:gap-3">
              {(
                [
                  ['Balance', acc.balance, ''],
                  ['In margin', D.from(acc.marginUsed).add(acc.orderHold).toString(), ''],
                  ['Withdrawable', acc.withdrawable, 'text-up'],
                ] as const
              ).map(([k, v, c]) => (
                <div key={k} className="flex min-w-0 items-baseline justify-between gap-3 rounded-[16px] bg-track p-3 sm:block md:p-4">
                  <div className="text-[13px] text-muted md:text-[14px]">{k}</div>
                  <div className={cx('num truncate text-[14px] sm:mt-1 md:text-[18px]', c)}>{fmtUsd(v)}</div>
                </div>
              ))}
            </div>
            {D.from(acc.credit).isPos() && (
              <p className="m-0 mt-3 text-[13px] text-muted">
                Your <span className="num text-fg">{fmtUsd(acc.credit)}</span> trading credit can be used to trade but cannot be withdrawn.
              </p>
            )}

            <fieldset disabled={!verified || cardBlocked} className={cx('m-0 mt-8 flex min-w-0 flex-col gap-8 border-0 p-0', (!verified || cardBlocked) && 'opacity-50')}>
              {fiat && (
                <Dropdown
                  label={tab === 'card' ? 'Card currency' : 'Payout currency'}
                  value={ccy}
                  onChange={(v) => setCurrency(v as PayoutCurrency)}
                  disabled={!verified || cardBlocked}
                  options={PAYOUT_CURRENCIES.map((c) => ({
                    id: c,
                    icon: <Badge>{CURRENCY_SYMBOL[c]}</Badge>,
                    title: `${c} · ${CURRENCY_NAME[c]}`,
                    sub: tab === 'card' ? 'The currency of your card' : `Paid by ${PAYOUT_RAIL[c]}`,
                  }))}
                />
              )}

              {!fiat && (
                <Dropdown
                  label="Coin and network"
                  value={assetId}
                  onChange={(v) => {
                    setAssetId(v as WithdrawalAssetId);
                    setAdding(false);
                    // A coin amount means nothing in another coin: back to USD (the default).
                    if (unit === 'coin') {
                      setUnit('USD');
                      setAmount('');
                    }
                  }}
                  disabled={!verified}
                  options={assetOptions(options)}
                />
              )}

              {fiat ? (
                <div className="flex min-w-0 flex-col gap-2.5">
                  {choices.length > 0 ? (
                    <Dropdown
                      label={tab === 'card' ? 'Refund to' : 'Pay to'}
                      value={payoutId}
                      onChange={setPayoutId}
                      disabled={!verified || cardBlocked}
                      options={choices.map((p) => {
                        return {
                          id: p.id,
                          icon: (
                            <Badge>
                              <AIcon name={p.kind === 'card' ? 'card' : 'bank'} size={16} />
                            </Badge>
                          ),
                          title: p.label,
                          sub: p.summary,
                        };
                      })}
                    />
                  ) : (
                    <>
                      <span className="text-[14px] font-semibold">{tab === 'card' ? 'Refund to' : 'Pay to'}</span>
                      <p className="m-0 text-[15px] text-muted">
                        {tab === 'card' ? `Add the ${ccy} card you deposited with.` : `Add a ${ccy} bank account in your name.`} You can save up to 20 bank accounts and cards.
                      </p>
                    </>
                  )}
                  {!adding && (
                    <button type="button" onClick={() => setAdding(true)} className="w-max text-[15px] font-semibold text-fg underline-offset-4 hover:underline">
                      {tab === 'card' ? '+ Add a card' : '+ Add a bank account'}
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex min-w-0 flex-col gap-2.5">
                  {cryptoChoices.length > 0 ? (
                    <Dropdown
                      label="To address"
                      value={addressId}
                      onChange={setAddressId}
                      disabled={!verified}
                      options={cryptoChoices.map((a) => {
                        return {
                          id: a.id,
                          icon: cryptoIcon(a.coin, a.network),
                          title: a.label,
                          sub: `${a.coin} · ${a.network} · ${short(a.address)}`,
                          keywords: a.address,
                        };
                      })}
                    />
                  ) : (
                    <>
                      <span className="text-[14px] font-semibold">To address</span>
                      <p className="m-0 text-[15px] text-muted">Add the {asset.label} wallet you want to withdraw to. You can save up to 20 addresses.</p>
                    </>
                  )}
                  {!adding && (
                    <button type="button" onClick={() => setAdding(true)} className="w-max text-[15px] font-semibold text-fg underline-offset-4 hover:underline">
                      + Add an address
                    </button>
                  )}
                </div>
              )}
              {adding &&
                (fiat ? (
                  <AddPayoutAccount
                    key={`${kind}-${ccy}`}
                    kind={kind}
                    currency={ccy}
                    onCancel={() => setAdding(false)}
                    onAdded={(p) => {
                      setPayouts([...(payouts ?? []), p]);
                      setPayoutId(p.id);
                      setAdding(false);
                    }}
                  />
                ) : (
                  <AddAddress
                    initial={assetId}
                    options={options}
                    onCancel={() => setAdding(false)}
                    onAdded={(a) => {
                      setAddresses([...(addresses ?? []), a]);
                      setAssetId(a.asset);
                      setAddressId(a.id);
                      setAdding(false);
                    }}
                  />
                ))}
              <Field
                label={
                  fiat || usdt ? (
                    fiat ? 'Amount (from your USDT balance)' : 'Amount'
                  ) : (
                    <span className="flex w-full flex-wrap items-center justify-between gap-2">
                      Amount
                      <Segmented label="Enter the amount in" size="sm" value={unit} onChange={switchUnit} options={[{ id: 'USD', label: 'USD' }, { id: 'coin', label: asset.coin }]} />
                    </span>
                  )
                }
                htmlFor="wd-amount"
                error={amountErr}
                hint={limitsHint || undefined}
              >
                <div className="field flex !h-[64px] items-center gap-2 md:!h-[72px]">
                  <span className="text-[20px]">{inCoin ? COIN_SIGN[asset.coin] ?? '' : fiat || !usdt ? '$' : '₮'}</span>
                  <NumInput
                    id="wd-amount"
                    placeholder={inCoin ? '0.00000000' : '0.00'}
                    value={amount}
                    onValue={(v) => setAmount(v.replace(/[^\d.]/g, ''))}
                    className="num min-w-0 flex-1 bg-transparent text-[20px] text-fg outline-none placeholder:text-muted"
                  />
                  <span className="text-[16px]">{inCoin ? asset.coin : fiat || !usdt ? 'USD' : 'USDT'}</span>
                </div>
                {!fiat && !usdt && (
                  <span className="num text-[15px]">
                    {inCoin ? `≈ ${amt ? fmtUsd(amt) : '$0.00'} from your balance` : `≈ ${receive ? coinMoney(receive.toString(), asset.coin) : `0 ${asset.coin}`} to your wallet`}
                  </span>
                )}
                <div className="grid grid-cols-4 gap-2 md:gap-3">
                  {[25, 50, 75, 100].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => {
                        const usd = free.mul(p).div(100, 'down').round(2, 'down');
                        setAmount(inCoin ? coinFor(usd) : usd.toFixed(2, 'down'));
                      }}
                      className="h-[42px] rounded-full bg-pill text-[15px] font-semibold md:text-[16px]"
                    >
                      {p === 100 ? 'Max' : `${p}%`}
                    </button>
                  ))}
                </div>
              </Field>
              <dl className="num m-0 flex flex-col gap-3 rounded-[20px] border border-line p-5 text-[16px] md:text-[17px]">
                {fiat ? (
                  <>
                    <div className="flex justify-between gap-4">
                      <dt className="font-sans">Withdrawal fee</dt>
                      <dd className="m-0">{fee !== null ? fmtUsd(fee) : '—'}</dd>
                    </div>
                    {ccy !== 'USD' && (
                      <div className="flex justify-between gap-4">
                        <dt className="font-sans">Rate (indicative)</dt>
                        <dd className="m-0">{rate ? `${CURRENCY_SYMBOL[ccy]}1 = $${fmt(rate, 4)}` : 'Set on the day we pay'}</dd>
                      </div>
                    )}
                    <div className="flex justify-between gap-4">
                      <dt className="font-sans">{ccy === 'USD' ? 'You receive' : 'You receive about'}</dt>
                      <dd className="m-0 font-semibold">{receive ? (receiveEst ? money(receiveEst.toFixed(2, 'down'), ccy) : `${fmtUsd(receive)} in ${ccy}`) : '—'}</dd>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between gap-4">
                      <dt className="font-sans">Network fee</dt>
                      <dd className="m-0">{coinMoney(fee!, asset.coin)}</dd>
                    </div>
                    {!usdt && (
                      <div className="flex justify-between gap-4">
                        <dt className="flex items-center gap-2 font-sans">
                          Rate
                          {liveRate && <span className="rounded-full bg-up-bg px-2 py-0.5 text-[12px] font-semibold text-up">Live</span>}
                        </dt>
                        <dd className="m-0">{rate ? `1 ${asset.coin} = ${fmtUsd(rate)}` : 'No live price right now'}</dd>
                      </div>
                    )}
                    {!usdt && (
                      <div className="flex justify-between gap-4">
                        <dt className="font-sans">Taken from your balance</dt>
                        <dd className="m-0">{amt ? fmtUsd(amt) : '—'}</dd>
                      </div>
                    )}
                    <div className="flex justify-between gap-4">
                      <dt className="font-sans">{usdt ? 'You receive' : inCoin ? 'You receive exactly' : 'You receive about'}</dt>
                      <dd className="m-0 font-semibold">{receive ? coinMoney(receive.toString(), asset.coin) : '—'}</dd>
                    </div>
                    {!usdt && (
                      <p className="m-0 font-sans text-[14px] text-muted">
                        {inCoin
                          ? `You get exactly the ${asset.coin} you entered. Its dollar value plus the network fee is taken from your balance at the live rate when you confirm.`
                          : `Your balance goes down by exactly this dollar amount. The ${asset.coin} you get is an estimate until we send it.`}
                      </p>
                    )}
                    {noPrice && <p className="m-0 font-sans text-[14px] text-warn">There is no live {asset.coin} price right now, so we can't quote this withdrawal. Try again in a minute.</p>}
                  </>
                )}
                {levelAfter && (
                  <div className="flex justify-between">
                    <dt className="font-sans">Margin level after</dt>
                    <dd className="m-0">{fmt(levelAfter, 0)}%</dd>
                  </div>
                )}
              </dl>
              {me.totpEnabled && (
                <Field label="Authenticator code" htmlFor="wd-code" hint="Or one of your backup codes">
                  <input id="wd-code" autoComplete="one-time-code" className="field num !w-56 text-center tracking-[.2em]" value={code} onChange={(e) => setCode(e.target.value.trim())} />
                </Field>
              )}
            </fieldset>
            {bonus && D.from(bonus.amount).isPos() && (
              <Alert tone="info" className="mt-8">
                Requesting a withdrawal removes your active trading bonus ({fmtUsd(bonus.amount)}). Any part backing open positions comes off when they close.
              </Alert>
            )}
            <div className="mt-8">
              <Notice msg={msg} onClose={() => setMsg(null)} />
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <PillLink to="/account/support?topic=funding" kind="secondary" className="!h-[56px] md:!h-[64px]">
                Help <AIcon name="right" />
              </PillLink>
              <PillButton type="submit" size="lg" busy={busy === 'submit'} disabled={!canSubmit}>
                Request withdrawal
              </PillButton>
            </div>
          </form>
        </Card>

        <div className="flex min-w-0 flex-col gap-8">
          <Card>
            <H2>Withdrawal requests</H2>
            <div className="mt-6">
              <List empty="No withdrawals yet">
                {(list ?? []).map((w) => (
                  <button
                    key={w.id}
                    type="button"
                    onClick={() => setTicketId(w.id)}
                    aria-label={`Open the ticket for withdrawal ${w.number}`}
                    className="group -mx-5 block w-[calc(100%+40px)] border-b border-line px-5 text-left first:rounded-t-[20px] last:rounded-b-[20px] last:border-b-0 hover:bg-track focus-visible:bg-track"
                  >
                    <Row
                      mono
                      icon={<WithdrawalIcon w={w} />}
                      title={
                        w.method === 'crypto'
                          ? w.coin === 'USDT' || !w.coin
                            ? `−${fmt(w.amount, 2)} USDT`
                            : `−${fmtUsd(w.amount)} → ${receivedText(w)}`
                          : `−${fmtUsd(w.amount)}${w.currency !== 'USD' && (w.amountSent ?? w.receiveEst) ? ` → ${w.amountSent ? '' : '≈ '}${money((w.amountSent ?? w.receiveEst)!, w.currency)}` : ''}`
                      }
                      sub={
                        <span>
                          {w.number} · {w.method === 'crypto' ? `${w.coin ?? 'USDT'} · ${w.network}` : `${w.method === 'card' ? 'Card' : 'Wire'} ${w.currency} · ${w.destination}`} · {when(w.createdAt)}
                        </span>
                      }
                      right={
                        <span className="flex shrink-0 items-center gap-2">
                          <Chip tone={STATUS[w.status][1]}>{STATUS[w.status][0]}</Chip>
                          <AIcon name="right" size={16} className="text-muted group-hover:text-fg" />
                        </span>
                      }
                    />
                  </button>
                ))}
              </List>
            </div>
          </Card>
          {ticket && (
            <WithdrawalTicket
              w={ticket}
              onClose={() => setTicketId(null)}
              onCancelled={() => {
                void reloadList();
                void reloadSummary();
              }}
            />
          )}
          {savedCount > 0 && (
            <Card>
              <H2 className="!text-[22px]">Saved payout methods</H2>
              <div className="mt-5">
                <List>
                  {(payouts ?? []).map((p) => (
                    <Row
                      key={p.id}
                      icon={<AIcon name={p.kind === 'card' ? 'card' : 'bank'} />}
                      title={p.label}
                      sub={
                        <span className="num">
                          {p.currency} · {p.summary}
                        </span>
                      }
                      right={
                        <button type="button" aria-label={`Remove ${p.label}`} onClick={() => void removePayout(p.id)} className="shrink-0 text-muted hover:text-down">
                          <AIcon name="trash" size={18} />
                        </button>
                      }
                    />
                  ))}
                  {(addresses ?? []).map((a) => (
                    <Row
                      key={a.id}
                      icon={cryptoIcon(a.coin, a.network)}
                      title={a.label}
                      sub={
                        <span className="num">
                          {a.coin} · {a.network} · {a.address}
                        </span>
                      }
                      right={
                        <span className="flex shrink-0 items-center gap-3">
                          {a.verifyStatus === 'verified' && <Chip tone="up">Verified</Chip>}
                          <button type="button" aria-label={`Remove ${a.label}`} onClick={() => void removeAddress(a.id)} className="shrink-0 text-muted hover:text-down">
                            <AIcon name="trash" size={18} />
                          </button>
                        </span>
                      }
                    />
                  ))}
                </List>
              </div>
            </Card>
          )}
          <Card>
            <H2 className="!text-[22px]">Before you withdraw</H2>
            <ul className="m-0 mt-5 flex list-none flex-col gap-3 p-0 text-[16px]">
              {CHECKLIST[tab].map((t) => (
                <li key={t} className="flex gap-3">
                  <AIcon name="check" className="shrink-0 text-up" />
                  {t}
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}
